import { Component, Input, OnDestroy } from '@angular/core';
import { ChestService, ImplantSize } from '../../services/chest.service';

interface EditedImage {
  id: string;
  originalUrl: string;
  editedUrl: string;
  prompt: string;
  timestamp: number;
}

interface ProcessingState {
  isProcessing: boolean;
  error: string | null;
}

interface SizeOption {
  value: ImplantSize;
  label: string;
  subtitle: string;
}

@Component({
  selector: 'app-chest-vison',
  templateUrl: './chest-vison.component.html',
  styleUrls: ['./chest-vison.component.scss']
})
export class ChestVisonComponent implements OnDestroy {
  @Input() showHeader = true;
  @Input() embedded = false;
  @Input() showMobileFooter = true;

  selectedFile: File | null = null;
  previewUrl: string | null = null;

  /**
   * Keeping this variable so existing history logic remains intact.
   * Now it becomes the generated label used for the history entry.
   */
  prompt = '';

  history: EditedImage[] = [];
  processing: ProcessingState = { isProcessing: false, error: null };
  currentResult: string | null = null;

  // --- Chest augmentation size controls (1..3) ---
  readonly sizeOptions: SizeOption[] = [
    { value: 1, label: 'Conservative Augmentation', subtitle: 'Size 1 • Subtle' },
    { value: 2, label: 'Moderate Augmentation (Default)', subtitle: 'Size 2 • Moderate' },
    { value: 3, label: 'Fuller Augmentation', subtitle: 'Size 3 • Strong' }
  ];

  // IMPORTANT: Must exist for template bindings
  selectedSize: ImplantSize = 2;

  constructor(private chestService: ChestService) {}

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!/^image\//i.test(file.type)) {
      this.processing = { isProcessing: false, error: 'Please upload a valid image file.' };
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.processing = { isProcessing: false, error: 'PNG, JPG or WebP only (max 10MB).' };
      return;
    }

    this.selectedFile = file;
    this.setPreviewUrl(URL.createObjectURL(file));
    this.currentResult = null;
    this.processing = { isProcessing: false, error: null };
  }

  async handleEdit(): Promise<void> {
    if (!this.selectedFile) return;

    this.processing = { isProcessing: true, error: null };

    try {
      const base64DataUrl = await this.fileToBase64(this.selectedFile);

      // History label mapped to server-side signature (size 1..3)
      this.prompt = this.buildHistoryLabel(this.selectedSize);

      // Calls ChestService -> lambda expects size (1..3)
      const editedBase64 = await this.chestService.applySize(
        base64DataUrl,
        this.selectedFile.type || 'image/png',
        this.selectedSize
      );

      this.currentResult = editedBase64;

      const newEntry: EditedImage = {
        id: crypto.randomUUID(),
        originalUrl: this.previewUrl || '',
        editedUrl: editedBase64,
        prompt: this.prompt,
        timestamp: Date.now()
      };

      this.history = [newEntry, ...this.history];
    } catch (err: any) {
      this.processing = { isProcessing: false, error: err?.message || 'Failed to generate chest preview.' };
      return;
    } finally {
      this.processing = { ...this.processing, isProcessing: false };
    }
  }

  clearSession(): void {
    this.selectedFile = null;
    this.currentResult = null;
    this.prompt = '';
    this.processing = { isProcessing: false, error: null };
    this.setPreviewUrl(null);

    // Reset to default (size 2)
    this.selectedSize = 2;
  }

  deleteHistoryItem(id: string): void {
    this.history = this.history.filter((item) => item.id !== id);
  }

  viewHistoryItem(url: string): void {
    this.currentResult = url;
  }

  // --------------------------
  // Helpers (used by template)
  // --------------------------
  sizeWord(level: ImplantSize): string {
    if (level === 1) return 'subtle';
    if (level === 2) return 'moderate';
    return 'strong';
  }

  sizeLabel(level: ImplantSize): string {
    return this.sizeOptions.find((x) => x.value === level)?.label || `Size ${level}`;
  }

  // --------------------------
  // Private internals
  // --------------------------
  private buildHistoryLabel(size: ImplantSize): string {
    return `${this.sizeLabel(size)} • Size ${size} (${this.sizeWord(size)})`;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  private setPreviewUrl(url: string | null): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = url;
  }

  private revokePreviewUrl(): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
}